import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { 
    ArrowLeft, Printer, FileText, Download, 
    CheckCircle, AlertTriangle, ChevronDown,
    Activity, Target, TrendingUp, Sparkles,
    Shield
} from 'lucide-react';
import {
    Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell
} from 'recharts';
import { useData } from '../../contexts/DataContext';
import { SCREENING_SECTIONS } from '../../lib/screeningQuestions';
import { evaluateScreeningAnswers } from '../../lib/screeningEvaluator';

// Per-patient result data (High-fidelity mocks for demo)
const PATIENT_RESULTS = {
    1: {
        name: 'Alex Johnson',
        assessment: 'Comprehensive Initial Evaluation',
        totalScore: 71,
        summary: 'Alex demonstrates strong independent skills in Communication and Self-Help, actively engaging in verbal tasks. Focus areas include Academics (specifically functional math concepts) and Social play interactions where prompted guidance is frequently required.',
        strengths: [
            'Excellent receptive language; identifies named objects with 90% accuracy.',
            'Can follow multi-step daily living routines (washing hands, packing bag).',
            'Demonstrates consistent eye contact during preferred activities.',
        ],
        support: [
            'Initiating social play with peers without adult facilitation.',
            'Number recognition and 1:1 correspondence above 5.',
            'Sustained attention in group activities longer than 5 minutes.',
        ],
        radar: [
            { subject: 'Communication', A: 85, fullMark: 100 },
            { subject: 'Social', A: 65, fullMark: 100 },
            { subject: 'Self-Help', A: 80, fullMark: 100 },
            { subject: 'Motor', A: 70, fullMark: 100 },
            { subject: 'Academics', A: 50, fullMark: 100 },
            { subject: 'Behavior', A: 75, fullMark: 100 },
        ],
        bars: [
            { name: 'Comm.', score: 85, fill: '#14b8a6' },
            { name: 'Social', score: 65, fill: '#f59e0b' },
            { name: 'Self-Help', score: 80, fill: '#14b8a6' },
            { name: 'Motor', score: 70, fill: '#0ea5e9' },
            { name: 'Academics', score: 50, fill: '#ef4444' },
            { name: 'Behavior', score: 75, fill: '#0ea5e9' },
        ],
    },
    2: {
        name: 'Mia Wong',
        assessment: 'Sensory Processing Profile',
        totalScore: 63,
        summary: 'Mia shows solid performance in Behavior and Social domains with good peer engagement skills. Key focus areas are Self-Help independence and Academics, where she benefits from additional scaffolding and structured routines.',
        strengths: [
            'Strong social engagement; easily initiates play with peers.',
            'Good behavioral regulation in structured classroom settings.',
            'Demonstrates enthusiasm and motivation during preferred tasks.',
        ],
        support: [
            'Independent self-help skills such as dressing and meal preparation.',
            'Sustained focus during non-preferred academic activities.',
            'Following multi-step verbal instructions without visual prompts.',
        ],
        radar: [
            { subject: 'Communication', A: 70, fullMark: 100 },
            { subject: 'Social', A: 80, fullMark: 100 },
            { subject: 'Self-Help', A: 45, fullMark: 100 },
            { subject: 'Motor', A: 65, fullMark: 100 },
            { subject: 'Academics', A: 55, fullMark: 100 },
            { subject: 'Behavior', A: 82, fullMark: 100 },
        ],
        bars: [
            { name: 'Comm.', score: 70, fill: '#14b8a6' },
            { name: 'Social', score: 80, fill: '#14b8a6' },
            { name: 'Self-Help', score: 45, fill: '#ef4444' },
            { name: 'Motor', score: 65, fill: '#0ea5e9' },
            { name: 'Academics', score: 55, fill: '#f59e0b' },
            { name: 'Behavior', score: 82, fill: '#14b8a6' },
        ],
    },
};

export default function Results() {
    const { profiles } = useData();
    const location = useLocation();
    const [selectedPatientId, setSelectedPatientId] = useState(1);

    // Dynamic data from navigation state if available
    const submission = location.state?.submission;
    const isScreening = submission?.type === 'Child Developmental Screening' || submission?.type === 'Child Developmental Screening (Final)';
    
    const data = useMemo(() => {
        if (submission) {
            const score = submission.score || 0;
            const answers = submission.answers || {};

            if (isScreening) {
                const evaluation = evaluateScreeningAnswers(answers, submission.remarks || {});
                return {
                    name: submission.patientName,
                    assessment: submission.type,
                    totalScore: evaluation.percentage,
                    summary: `Child Developmental Screening results for ${submission.patientName}. The screening shows a ${evaluation.percentage}% developmental milestone score (${evaluation.score} of ${evaluation.maxScore} points). Clinical concern level: ${evaluation.riskLevel}. ${evaluation.riskDescription}`,
                    strengths: evaluation.domainBreakdown
                        .filter(d => d.percentage >= 75)
                        .map(d => `Strong milestone attainment in ${d.name} (${d.percentage}%)`),
                    support: evaluation.domainBreakdown
                        .filter(d => d.percentage < 75)
                        .map(d => `Targeted support suggested for ${d.name} (${d.percentage}%)`),
                    radar: evaluation.domainBreakdown.map(d => ({
                        subject: d.name.replace(' Skills', '').replace(' Engagement', ''),
                        A: d.percentage,
                        fullMark: 100
                    })),
                    bars: evaluation.domainBreakdown.map((d) => ({
                        name: d.name.substring(0, 5).toUpperCase(),
                        fullName: d.name,
                        score: d.percentage,
                        fill: d.color || '#6366f1'
                    }))
                };
            }

            return {
                name: submission.patientName,
                assessment: submission.type,
                totalScore: Math.round((Math.min(score, 36) / 36) * 100), 
                summary: `This report reflects the evaluation of ${submission.patientName} on ${new Date().toLocaleDateString()}. The assessment indicates a performance level of ${Math.round((Math.min(score, 36) / 36) * 100)}% across the clinical domains.`,
                strengths: ['Demonstrated high engagement during assessment.', 'Consistent response to clinical prompts.'],
                support: ['Continue focus on domain-specific skill building.', 'Monitor progress in social interactions.'],
                radar: [
                    { subject: 'Communication', A: 80, fullMark: 100 },
                    { subject: 'Social', A: 60, fullMark: 100 },
                    { subject: 'Self-Help', A: 70, fullMark: 100 },
                    { subject: 'Motor', A: 75, fullMark: 100 },
                    { subject: 'Academics', A: 50, fullMark: 100 },
                    { subject: 'Behavior', A: 85, fullMark: 100 },
                ],
                bars: [
                    { name: 'Comm.', score: 80, fill: '#14b8a6' },
                    { name: 'Social', score: 60, fill: '#f59e0b' },
                    { name: 'Self-Help', score: 70, fill: '#14b8a6' },
                    { name: 'Motor', score: 75, fill: '#0ea5e9' },
                    { name: 'Academics', score: 50, fill: '#ef4444' },
                    { name: 'Behavior', score: 85, fill: '#14b8a6' },
                ]
            };
        }
        return PATIENT_RESULTS[selectedPatientId] || PATIENT_RESULTS[1];
    }, [selectedPatientId, submission, isScreening]);

    return (
        <div className="space-y-10 pb-20 font-inter">
            {/* Header Area */}
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-6 print:hidden">
                <div className="flex items-center gap-5">
                    <Link to="/Screening_test/app/assessments">
                        <Button variant="ghost" size="icon" className="h-12 w-12 bg-white rounded-2xl shadow-soft border border-slate-100 text-slate-400 hover:text-slate-900 transition-all">
                            <ArrowLeft className="h-6 w-6" />
                        </Button>
                    </Link>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                             <h1 className="text-4xl font-black text-slate-900 font-outfit tracking-tight uppercase">Outcome Analysis</h1>
                             <Badge className="bg-brand-50 text-brand-600 border-brand-100 rounded-lg text-[10px] font-black uppercase tracking-widest px-2 py-0.5">Clinical Grade</Badge>
                        </div>
                        <p className="text-slate-500 font-medium">{data.name} • {data.assessment}</p>
                    </div>
                </div>
                
                <div className="flex items-center gap-3 flex-wrap">
                    {!submission && (
                        <div className="relative group">
                            <select
                                value={selectedPatientId}
                                onChange={e => setSelectedPatientId(Number(e.target.value))}
                                className="h-14 pl-6 pr-14 rounded-2xl bg-white border border-slate-200 focus:ring-2 focus:ring-brand-500 font-bold text-slate-900 appearance-none shadow-soft transition-all cursor-pointer min-w-[240px]"
                            >
                                {profiles.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 group-hover:text-brand-500 transition-colors pointer-events-none" />
                        </div>
                    )}
                    <Button variant="outline" className="h-14 rounded-2xl px-6 border-slate-200 bg-white font-black text-xs uppercase tracking-widest text-slate-600 shadow-sm hover:bg-slate-50" onClick={() => window.print()}>
                        <Printer className="mr-3 h-4 w-4" /> Print Evaluation
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-10">
                {/* Main Content Column */}
                <div className="xl:col-span-8 space-y-10">
                    
                    {/* Executive Summary Card */}
                    <Card className="border-0 shadow-soft overflow-hidden rounded-[2.5rem] bg-white border border-slate-50 relative group">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-brand-50/30 rounded-full blur-[100px] -mr-32 -mt-32 pointer-events-none"></div>
                        <div className="p-10 relative z-10">
                            <div className="flex flex-col md:flex-row justify-between items-start gap-10">
                                <div className="flex-1 space-y-6">
                                    <div className="flex items-center gap-3">
                                         <div className="h-10 w-10 rounded-xl bg-slate-900 flex items-center justify-center text-brand-400 shadow-lg shrink-0">
                                            <Sparkles className="h-5 w-5" />
                                         </div>
                                         <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Executive Narrative</span>
                                    </div>
                                    <h2 className="text-3xl font-black text-slate-900 font-outfit tracking-tight">Clinical Interpretation</h2>
                                    <p className="text-slate-500 text-lg font-light leading-relaxed italic border-l-4 border-brand-200 pl-6">
                                        "{data.summary}"
                                    </p>
                                </div>
                                <div className="shrink-0 flex flex-col items-center justify-center p-8 bg-slate-900 rounded-[2.5rem] text-white shadow-premium min-w-[200px] border border-white/5 relative overflow-hidden">
                                     <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-transparent"></div>
                                     <p className="relative z-10 text-[10px] font-black uppercase tracking-[0.3em] text-brand-400 mb-2">Aggregate Index</p>
                                     <h3 className="relative z-10 text-7xl font-black font-outfit leading-none">{data.totalScore}<span className="text-2xl text-brand-500 font-light">%</span></h3>
                                     <div className="relative z-10 mt-4 flex items-center gap-2">
                                         <TrendingUp className="h-4 w-4 text-green-400" />
                                         <span className="text-[10px] font-black uppercase text-green-400 tracking-tighter">Normalised Progression</span>
                                     </div>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Detailed Question Responses (Screening Only) */}
                    {isScreening && (
                        <Card className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 overflow-hidden">
                            <CardHeader className="p-10 pb-4 border-b border-slate-50 bg-slate-50/30">
                                <CardTitle className="text-2xl font-black text-slate-900 font-outfit uppercase tracking-tight">Response Breakdown</CardTitle>
                                <CardDescription className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Item-level Screening Data</CardDescription>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="divide-y divide-slate-50">
                                    {SCREENING_SECTIONS.map((section) => (
                                        <div key={section.id} className="p-10 space-y-8">
                                            <div className="flex items-center gap-3">
                                                <div className="h-1.5 w-8 bg-brand-500 rounded-full"></div>
                                                <h4 className="text-xs font-black text-brand-600 uppercase tracking-widest">{section.title}</h4>
                                            </div>
                                            <div className="grid gap-4">
                                                {section.questions.map((q) => {
                                                    const answer = submission.answers[q.id];
                                                    const isCritical = answer === 'No' || answer === 'Not Sure';
                                                    const isText = q.type === 'textarea' || q.type === 'text';
                                                    
                                                    if (isText && !answer) return null;

                                                    return (
                                                        <div key={q.id} className={`p-6 rounded-[2rem] border transition-all ${isCritical && !isText ? 'bg-red-50/30 border-red-100/50' : 'bg-slate-50/50 border-slate-100/50'}`}>
                                                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                                <p className={`font-bold text-sm ${isCritical && !isText ? 'text-red-900' : 'text-slate-700'}`}>{q.label}</p>
                                                                {!isText && (
                                                                    <Badge className={`rounded-xl px-4 py-1 text-[10px] font-black uppercase tracking-widest shrink-0 ${
                                                                        answer === 'Yes' ? 'bg-green-100 text-green-700 border-green-200' : 
                                                                        answer === 'No' ? 'bg-red-100 text-red-700 border-red-200' : 
                                                                        'bg-amber-100 text-amber-700 border-amber-200'
                                                                    }`}>
                                                                        {answer || 'N/A'}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            {isText && answer && (
                                                                 <div className="mt-4 p-4 rounded-2xl bg-white border border-slate-100 text-xs text-slate-600 font-medium leading-relaxed">
                                                                    {answer}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Insights Grid */}
                    <div className="grid md:grid-cols-2 gap-10">
                        <Card className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 overflow-hidden group">
                            <CardHeader className="p-8 pb-4">
                                <div className="flex items-center gap-3 mb-2">
                                     <div className="h-8 w-8 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600 transition-colors group-hover:bg-teal-600 group-hover:text-white">
                                        <CheckCircle className="h-4 w-4" />
                                     </div>
                                     <CardTitle className="text-xl font-black text-slate-900 font-outfit uppercase tracking-tight">Core Strengths</CardTitle>
                                </div>
                                <CardDescription className="text-slate-400 font-medium">Areas of functional mastery</CardDescription>
                            </CardHeader>
                            <CardContent className="p-8 pt-0">
                                <div className="space-y-4 mt-4">
                                    {(data.strengths.length > 0 ? data.strengths : ['Engagement', 'Responsiveness']).map((s, i) => (
                                        <div key={i} className="flex items-start gap-4 bg-teal-50/30 p-4 rounded-3xl border border-teal-50 transition-all hover:bg-teal-50/50">
                                             <div className="shrink-0 h-6 w-6 rounded-lg bg-teal-100 flex items-center justify-center text-teal-700 font-black text-[10px] uppercase">S{i+1}</div>
                                             <p className="text-sm font-bold text-slate-700 leading-relaxed">{s}</p>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 overflow-hidden group">
                            <CardHeader className="p-8 pb-4">
                                <div className="flex items-center gap-3 mb-2">
                                     <div className="h-8 w-8 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 transition-colors group-hover:bg-amber-600 group-hover:text-white">
                                        <AlertTriangle className="h-4 w-4" />
                                     </div>
                                     <CardTitle className="text-xl font-black text-slate-900 font-outfit uppercase tracking-tight">Focus Points</CardTitle>
                                </div>
                                <CardDescription className="text-slate-400 font-medium">Priority intervention modules</CardDescription>
                            </CardHeader>
                            <CardContent className="p-8 pt-0">
                                <div className="space-y-4 mt-4">
                                    {(data.support.length > 0 ? data.support : ['Specific Domain Focus', 'Routine Integration']).map((s, i) => (
                                        <div key={i} className="flex items-start gap-4 bg-amber-50/30 p-4 rounded-3xl border border-amber-50 transition-all hover:bg-amber-50/50">
                                             <div className="shrink-0 h-6 w-6 rounded-lg bg-amber-100 flex items-center justify-center text-amber-700 font-black text-[10px] uppercase">P{i+1}</div>
                                             <p className="text-sm font-bold text-slate-700 leading-relaxed">{s}</p>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Detailed Breakdown Chart */}
                    <Card className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 overflow-hidden">
                        <CardHeader className="p-10 pb-4 flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="text-2xl font-black text-slate-900 font-outfit uppercase tracking-tight">Performance Matrix</CardTitle>
                                <CardDescription className="text-slate-400 font-medium uppercase tracking-widest text-[10px]">Maturity distribution across primary markers</CardDescription>
                            </div>
                            <Activity className="h-8 w-8 text-slate-100" />
                        </CardHeader>
                        <CardContent className="p-10">
                            <div className="h-[400px] w-full mt-4">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={data.bars} margin={{ top: 0, right: 30, left: 10, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 800 }} dy={10} />
                                        <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 800 }} />
                                        <Tooltip 
                                            cursor={{ fill: '#f8fafc', radius: 16 }} 
                                            contentStyle={{ borderRadius: '24px', border: 'none', boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.1) ' }} 
                                        />
                                        <Bar dataKey="score" radius={[16, 16, 16, 16]} barSize={isScreening ? 30 : 40}>
                                            {data.bars.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.fill} fillOpacity={0.8} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Sidebar Column */}
                <div className="xl:col-span-4 space-y-10">
                    
                    {/* Visual Profile Map */}
                    <Card className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 overflow-hidden">
                        <CardHeader className="p-8 pb-4">
                            <CardTitle className="text-xl font-black text-slate-900 font-outfit uppercase tracking-tight">Maturity Map</CardTitle>
                        </CardHeader>
                        <CardContent className="p-8">
                            <div className="h-[300px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <RadarChart cx="50%" cy="50%" outerRadius="75%" data={data.radar}>
                                        <PolarGrid stroke="#f1f5f9" />
                                        <PolarAngleAxis dataKey="subject" tick={{ fill: '#94a3b8', fontSize: 9, fontWeight: 800 }} />
                                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                                        <Radar name={data.name} dataKey="A" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.3} strokeWidth={4} />
                                        <Tooltip contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' }} />
                                    </RadarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-8 flex items-center justify-between text-center gap-4">
                                <div className="flex-1 p-4 rounded-3xl bg-slate-50 border border-slate-100">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Complexity</p>
                                    <p className="text-xl font-black text-slate-700 font-outfit">Moderate</p>
                                </div>
                                <div className="flex-1 p-4 rounded-3xl bg-slate-50 border border-slate-100">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Reliability</p>
                                    <p className="text-xl font-black text-slate-700 font-outfit">98%</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Intervention Call-to-Action */}
                    <Card className="bg-slate-900 text-white border-0 shadow-premium rounded-[2.5rem] overflow-hidden relative">
                         <div className="absolute top-0 right-0 w-32 h-32 bg-brand-500/10 rounded-full blur-3xl"></div>
                        <CardContent className="p-10 relative z-10">
                            <div className="h-14 w-14 rounded-2xl bg-white/10 flex items-center justify-center mb-8 border border-white/10 shadow-inner text-brand-400">
                                <Target className="h-7 w-7" />
                            </div>
                            <h3 className="text-2xl font-black font-outfit mb-3 uppercase tracking-tight">Intervention</h3>
                            <p className="text-slate-400 text-sm leading-relaxed mb-8 font-light">
                                Therapeutic objectives should prioritize <span className="text-brand-400 font-bold uppercase tracking-widest">{isScreening ? 'Motor & Social' : 'Functional'}</span> markers to capitalize on emerging linguistic strengths.
                            </p>
                            <Link to="/Screening_test/app/iep">
                                <Button className="w-full bg-brand-600 hover:bg-brand-700 text-white rounded-2xl h-14 font-black text-xs uppercase tracking-widest shadow-lg transition-all border-none">
                                    Draft Clinical Plan
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>

                    {/* Security Footnote */}
                    <div className="p-8 rounded-[2.5rem] bg-white/40 border border-slate-100 backdrop-blur-sm flex items-start gap-4">
                         <Shield className="h-6 w-6 text-slate-300 shrink-0" />
                         <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Data Integrity</p>
                            <p className="text-[10px] text-slate-400 font-medium leading-relaxed italic">
                                Information is encrypted and intended for authorized clinical personnel under Melete-Nova governance.
                            </p>
                         </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
